import { isOnStep } from '@cb/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api.ts';
import { useRefreshBookings } from '../queries.ts';
import { addMinutes, fmtDate, fmtTime, fromInputs, toDateInput, toTimeInput } from '../time.ts';
import { isStaff, type Booking, type Me, type Room } from '../types.ts';
import { useErrorText } from './ErrorText.tsx';
import { Modal } from './Modal.tsx';
import { useToast } from './Toast.tsx';

interface Clash {
  start: string;
  end: string;
  reason: 'booking' | 'block';
}

interface TenantOption {
  id: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
  role: string;
  status: string;
}

export function BookingDialog({
  me,
  rooms,
  initial,
  editing,
  onClose,
}: {
  me: Me;
  rooms: Room[];
  initial: { roomId?: string; start?: Date; end?: Date; attendees?: number };
  editing?: Booking;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshBookings();
  const errorText = useErrorText();
  const staff = isStaff(me);
  const bookable = rooms.filter((r) => r.canBook);
  const start0 = editing ? new Date(editing.start) : (initial.start ?? new Date());
  const end0 = editing ? new Date(editing.end) : (initial.end ?? addMinutes(start0, 60));

  const [roomId, setRoomId] = useState(editing?.roomId ?? initial.roomId ?? bookable[0]?.id ?? '');
  const [startDate, setStartDate] = useState(toDateInput(start0));
  const [startTime, setStartTime] = useState(toTimeInput(start0));
  const [endDate, setEndDate] = useState(toDateInput(end0));
  const [endTime, setEndTime] = useState(toTimeInput(end0));
  const [title, setTitle] = useState(editing?.title ?? '');
  const [attendees, setAttendees] = useState(String(editing?.attendees ?? initial.attendees ?? 2));
  const [repeat, setRepeat] = useState<'none' | 'count' | 'until'>('none');
  const [count, setCount] = useState('4');
  const [until, setUntil] = useState(toDateInput(addMinutes(start0, 4 * 7 * 1440)));
  const [userId, setUserId] = useState(editing?.userId ?? '');
  const [override, setOverride] = useState(false);
  const [scope, setScope] = useState<'one' | 'following'>('one');
  const [error, setError] = useState<string | null>(null);
  const [clashes, setClashes] = useState<Clash[]>([]);
  const [needsOverride, setNeedsOverride] = useState(false);
  const [busy, setBusy] = useState(false);

  const tenants = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get<TenantOption[]>('/api/admin/users'),
    enabled: staff && !editing,
  });

  const room = rooms.find((r) => r.id === roomId);
  const floors = [...new Set(bookable.map((r) => r.floor))].sort((a, b) => a - b);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setClashes([]);
    const start = fromInputs(startDate, startTime);
    const end = fromInputs(endDate, endTime);
    if (!start || !end || end <= start) return setError(t('errors.validation'));
    if (!isOnStep(start) || !isOnStep(end)) return setError(t('errors.validation'));
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/api/bookings/${editing.id}`, {
          roomId,
          start: start.toISOString(),
          end: end.toISOString(),
          title: title.trim(),
          attendees: Number(attendees),
          overrideCapacity: override,
          scope,
        });
        toast(t('common.saved'));
      } else {
        const r = await api.post<{ ids: string[] }>('/api/bookings', {
          roomId,
          start: start.toISOString(),
          end: end.toISOString(),
          title: title.trim(),
          attendees: Number(attendees),
          overrideCapacity: override,
          ...(staff && userId ? { userId } : {}),
          ...(repeat === 'count' ? { repeat: { count: Number(count) } } : {}),
          ...(repeat === 'until' ? { repeat: { until } } : {}),
        });
        toast(
          r.ids.length > 1
            ? t('booking.bookedSeries', { count: r.ids.length })
            : t('booking.booked'),
        );
      }
      refresh();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'series_conflicts') {
        setClashes((err.details as { clashes: Clash[] }).clashes);
      } else if (err instanceof ApiError && err.code === 'over_capacity' && staff) {
        setNeedsOverride(true);
        setError(t('booking.capacity', { count: (err.details as { capacity: number }).capacity }));
      } else {
        setError(errorText(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={editing ? t('booking.edit') : t('booking.new')} onClose={onClose}>
      <form onSubmit={submit}>
        <label>
          {t('booking.room')}
          <select
            aria-label={t('booking.room')}
            value={roomId}
            onChange={(e) => setRoomId(e.target.value)}
            required
          >
            {floors.map((f) => (
              <optgroup key={f} label={`${t('common.floor')} ${f}`}>
                {bookable
                  .filter((r) => r.floor === f)
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name} · {r.capacity} {t('common.people')}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
        </label>
        <div className="row">
          <label>
            {t('booking.startDate')}
            <input
              type="date"
              required
              value={startDate}
              onChange={(e) => {
                const moved = endDate === startDate;
                setStartDate(e.target.value);
                if (moved) setEndDate(e.target.value);
              }}
            />
          </label>
          <label>
            {t('booking.startTime')}
            <input
              type="time"
              step={900}
              required
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </label>
        </div>
        <div className="row">
          <label>
            {t('booking.endDate')}
            <input
              type="date"
              required
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
          <label>
            {t('booking.endTime')}
            <input
              type="time"
              step={900}
              required
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </label>
        </div>
        <label>
          {t('booking.title')}
          <input
            maxLength={120}
            value={title}
            placeholder={t('booking.titlePlaceholder')}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <div className="row">
          <label>
            {t('booking.attendees')}
            <input
              type="number"
              min={1}
              max={500}
              required
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              aria-describedby="cap-hint"
            />
            {room && (
              <span className="k" id="cap-hint">
                {t('booking.capacity', { count: room.capacity })}
              </span>
            )}
          </label>
          {!editing && (
            <label>
              {t('booking.repeat')}
              <select
                aria-label={t('booking.repeat')}
                value={repeat}
                onChange={(e) => setRepeat(e.target.value as typeof repeat)}
              >
                <option value="none">{t('booking.repeatNone')}</option>
                <option value="count">{t('booking.repeatCount')}</option>
                <option value="until">{t('booking.repeatUntil')}</option>
              </select>
            </label>
          )}
        </div>
        {room && (
          <p className="k" id="cap-hint" style={{ marginTop: -6 }}>
            {t('booking.capacity', { count: room.capacity })}
          </p>
        )}
        {repeat === 'count' && (
          <label>
            {t('booking.times')}
            <input
              type="number"
              min={2}
              max={52}
              value={count}
              onChange={(e) => setCount(e.target.value)}
            />
          </label>
        )}
        {repeat === 'until' && (
          <label>
            {t('booking.until')}
            <input
              type="date"
              min={startDate}
              value={until}
              onChange={(e) => setUntil(e.target.value)}
            />
          </label>
        )}
        {editing?.seriesId && (
          <fieldset>
            <legend>{t('booking.scopeQuestion')}</legend>
            <label className="check">
              <input
                type="radio"
                name="scope"
                checked={scope === 'one'}
                onChange={() => setScope('one')}
              />
              {t('booking.cancelOne')}
            </label>
            <label className="check">
              <input
                type="radio"
                name="scope"
                checked={scope === 'following'}
                onChange={() => setScope('following')}
              />
              {t('booking.cancelFollowing')}
            </label>
          </fieldset>
        )}
        {staff && !editing && (
          <label>
            {t('booking.forUser')}
            <select
              aria-label={t('booking.forUser')}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              <option value="">{t('booking.forMe')}</option>
              {(tenants.data ?? [])
                .filter(
                  (u) =>
                    u.role === 'tenant' && u.status !== 'inactive' && u.status !== 'anonymized',
                )
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName}
                    {u.companyName ? ` · ${u.companyName}` : ''}
                  </option>
                ))}
            </select>
          </label>
        )}
        {staff && needsOverride && (
          <label className="check">
            <input
              type="checkbox"
              checked={override}
              onChange={(e) => setOverride(e.target.checked)}
            />
            {t('booking.override')}
          </label>
        )}
        {error && (
          <p className="err" role="alert">
            {error}
          </p>
        )}
        {clashes.length > 0 && (
          <div className="err" role="alert">
            <p>{t('booking.clashes')}</p>
            <ul>
              {clashes.map((c) => (
                <li key={c.start}>
                  {fmtDate(c.start)} {fmtTime(c.start)}–{fmtTime(c.end)} (
                  {c.reason === 'block' ? t('booking.clashBlock') : t('booking.clashBooking')})
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="row">
          <button type="button" className="btn alt" onClick={onClose}>
            {t('common.close')}
          </button>
          <button className="btn" disabled={busy || !roomId}>
            {editing ? t('booking.saveChanges') : t('booking.book')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
